/* eslint-disable react-refresh/only-export-components --
   Intentional shared module: WHS label/colour maps live alongside a few tiny
   presentational Badge primitives so every page stays consistent. The
   react-refresh rule is a dev-only HMR nicety with no runtime impact, and
   splitting would fan the imports across ~17 pages for no benefit. */
// ============================================================================
// WHS shared labels, colour classes, and small presentational primitives used
// across every WHS page. Keeping them in one place means the language and
// colour coding stay consistent (ISO 45001 / AU WHS terminology).
// ============================================================================
import type {
  ReportType, ReportSeverity, ReportStatus, ReportTreatment, RiskLevel,
  ActionStatus, ActionPriority, ActionType, HierarchyControl, ResponsibleRole,
  RiskAssessmentType, RiskAssessmentStatus, InspectionStatus, InspectionResult,
  AuditType, AuditStatus, FindingType, DocumentType, DocumentStatus,
  EmergencyPlanType, ObjectiveStatus, InvestigationMethod,
} from '../../types/whs';

export const REPORT_TYPE_LABEL: Record<ReportType, string> = {
  incident: 'Incident',
  near_miss: 'Near miss',
  hazard: 'Hazard',
  illness: 'Work-related illness',
  unsafe_act: 'Unsafe act',
  unsafe_condition: 'Unsafe condition',
  property_damage: 'Property damage',
  environmental: 'Environmental',
};

export const SEVERITY_LABEL: Record<ReportSeverity, string> = {
  negligible: 'Negligible',
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  severe: 'Severe',
};

export const SEVERITY_CLASS: Record<ReportSeverity, string> = {
  negligible: 'bg-neutral-100 text-neutral-700',
  minor: 'bg-sky-100 text-sky-800',
  moderate: 'bg-amber-100 text-amber-800',
  major: 'bg-orange-100 text-orange-800',
  severe: 'bg-red-100 text-red-800',
};

export const TREATMENT_LABEL: Record<ReportTreatment, string> = {
  none: 'No treatment',
  first_aid: 'First aid',
  medical: 'Medical treatment',
  hospital: 'Hospitalisation',
  fatality: 'Fatality',
};

export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under review',
  investigating: 'Investigating',
  actioned: 'Actioned',
  closed: 'Closed',
  rejected: 'Rejected',
};

export const REPORT_STATUS_CLASS: Record<ReportStatus, string> = {
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-indigo-100 text-indigo-800',
  investigating: 'bg-amber-100 text-amber-800',
  actioned: 'bg-teal-100 text-teal-800',
  closed: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-neutral-200 text-neutral-700',
};

export const RISK_LEVEL_LABEL: Record<RiskLevel, string> = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  extreme: 'Extreme',
};

export const RISK_LEVEL_CLASS: Record<RiskLevel, string> = {
  low: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  moderate: 'bg-amber-100 text-amber-800 border-amber-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  extreme: 'bg-red-100 text-red-800 border-red-300',
};

// Solid cell colour for the risk matrix grid.
export const RISK_LEVEL_CELL: Record<RiskLevel, string> = {
  low: 'bg-emerald-400/80 text-emerald-950',
  moderate: 'bg-amber-400/80 text-amber-950',
  high: 'bg-orange-500/80 text-white',
  extreme: 'bg-red-600/85 text-white',
};

export const ACTION_STATUS_LABEL: Record<ActionStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
  verified: 'Verified',
  cancelled: 'Cancelled',
};

export const ACTION_STATUS_CLASS: Record<ActionStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-teal-100 text-teal-800',
  verified: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-neutral-200 text-neutral-600',
};

export const PRIORITY_LABEL: Record<ActionPriority, string> = {
  low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
};

export const PRIORITY_CLASS: Record<ActionPriority, string> = {
  low: 'bg-neutral-100 text-neutral-700',
  medium: 'bg-sky-100 text-sky-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export const ACTION_TYPE_LABEL: Record<ActionType, string> = {
  corrective: 'Corrective', preventive: 'Preventive', improvement: 'Improvement',
};

export const HIERARCHY_LABEL: Record<HierarchyControl, string> = {
  elimination: '1. Elimination',
  substitution: '2. Substitution',
  engineering: '3. Engineering controls',
  administrative: '4. Administrative controls',
  ppe: '5. PPE',
};

export const RESPONSIBLE_ROLE_LABEL: Record<ResponsibleRole, string> = {
  whs_manager: 'WHS Manager',
  deputy: 'Deputy WHS Manager',
  hsr: 'Health & Safety Representative',
  first_aider: 'First Aider',
  fire_warden: 'Fire Warden',
  committee_member: 'WHS Committee Member',
  other: 'Other',
};

export const RA_TYPE_LABEL: Record<RiskAssessmentType, string> = {
  jsa: 'Job Safety Analysis (JSA)',
  swms: 'Safe Work Method Statement (SWMS)',
  generic: 'Generic risk assessment',
  plant: 'Plant / equipment',
  chemical: 'Chemical / hazardous substance',
  manual_handling: 'Manual handling',
  psychosocial: 'Psychosocial',
};

export const RA_STATUS_LABEL: Record<RiskAssessmentStatus, string> = {
  draft: 'Draft', active: 'Active', under_review: 'Under review', archived: 'Archived',
};

export const RA_STATUS_CLASS: Record<RiskAssessmentStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  active: 'bg-emerald-100 text-emerald-800',
  under_review: 'bg-amber-100 text-amber-800',
  archived: 'bg-neutral-200 text-neutral-500',
};

export const INSPECTION_STATUS_LABEL: Record<InspectionStatus, string> = {
  scheduled: 'Scheduled', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled',
};

export const INSPECTION_STATUS_CLASS: Record<InspectionStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-neutral-200 text-neutral-600',
};

export const INSPECTION_RESULT_LABEL: Record<InspectionResult, string> = {
  pass: 'Pass', fail: 'Fail', na: 'N/A',
};

export const AUDIT_TYPE_LABEL: Record<AuditType, string> = {
  internal: 'Internal', external: 'External', compliance: 'Compliance', management_system: 'Management system',
};

export const AUDIT_STATUS_LABEL: Record<AuditStatus, string> = {
  planned: 'Planned', in_progress: 'In progress', completed: 'Completed',
};

export const AUDIT_STATUS_CLASS: Record<AuditStatus, string> = {
  planned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

export const FINDING_TYPE_LABEL: Record<FindingType, string> = {
  nonconformity_major: 'Major non-conformity',
  nonconformity_minor: 'Minor non-conformity',
  observation: 'Observation',
  opportunity: 'Opportunity for improvement',
  conformity: 'Conformity',
};

export const FINDING_TYPE_CLASS: Record<FindingType, string> = {
  nonconformity_major: 'bg-red-100 text-red-800',
  nonconformity_minor: 'bg-orange-100 text-orange-800',
  observation: 'bg-amber-100 text-amber-800',
  opportunity: 'bg-sky-100 text-sky-800',
  conformity: 'bg-emerald-100 text-emerald-800',
};

export const DOC_TYPE_LABEL: Record<DocumentType, string> = {
  policy: 'Policy',
  procedure: 'Procedure',
  swms: 'SWMS',
  sop: 'SOP',
  form: 'Form',
  sds: 'Safety Data Sheet',
  register: 'Register',
  emergency_plan: 'Emergency plan',
  other: 'Other',
};

export const DOC_STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: 'Draft', current: 'Current', under_review: 'Under review', archived: 'Archived', superseded: 'Superseded',
};

export const DOC_STATUS_CLASS: Record<DocumentStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  current: 'bg-emerald-100 text-emerald-800',
  under_review: 'bg-amber-100 text-amber-800',
  archived: 'bg-neutral-200 text-neutral-500',
  superseded: 'bg-neutral-200 text-neutral-500',
};

export const EMERGENCY_TYPE_LABEL: Record<EmergencyPlanType, string> = {
  evacuation: 'Evacuation',
  fire: 'Fire',
  first_aid: 'First aid',
  chemical_spill: 'Chemical spill',
  severe_weather: 'Severe weather',
  other: 'Other',
};

export const OBJECTIVE_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  on_track: 'On track', at_risk: 'At risk', behind: 'Behind', achieved: 'Achieved', not_achieved: 'Not achieved',
};

export const OBJECTIVE_STATUS_CLASS: Record<ObjectiveStatus, string> = {
  on_track: 'bg-emerald-100 text-emerald-800',
  at_risk: 'bg-amber-100 text-amber-800',
  behind: 'bg-red-100 text-red-800',
  achieved: 'bg-teal-100 text-teal-800',
  not_achieved: 'bg-neutral-200 text-neutral-600',
};

export const INVESTIGATION_METHOD_LABEL: Record<InvestigationMethod, string> = {
  icam: 'ICAM',
  five_why: '5 Whys',
  fishbone: 'Fishbone (Ishikawa)',
  other: 'Other',
};

export const HAZARD_CATEGORIES: string[] = [
  'manual_handling', 'chemical', 'plant', 'electrical', 'biological', 'psychosocial',
  'environmental', 'height', 'noise', 'slips_trips', 'vehicle', 'other',
];

export const HAZARD_CATEGORY_LABEL: Record<string, string> = {
  manual_handling: 'Manual handling',
  chemical: 'Chemical',
  plant: 'Plant / equipment',
  electrical: 'Electrical',
  biological: 'Biological',
  psychosocial: 'Psychosocial',
  environmental: 'Environmental',
  height: 'Working at height',
  noise: 'Noise',
  slips_trips: 'Slips, trips & falls',
  vehicle: 'Vehicle / traffic',
  other: 'Other',
};

// ─── Small presentational primitives ─────────────────────────────────────────
export function Badge({ label, className }: { label: string; className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className || 'bg-neutral-100 text-neutral-700'}`}>
      {label}
    </span>
  );
}

export function StatusBadge({ status }: { status: ReportStatus }) {
  return <Badge label={REPORT_STATUS_LABEL[status]} className={REPORT_STATUS_CLASS[status]} />;
}

export function RiskBadge({ level }: { level: RiskLevel | null }) {
  if (!level) return <Badge label="Not rated" className="bg-neutral-100 text-neutral-500" />;
  return <Badge label={RISK_LEVEL_LABEL[level]} className={RISK_LEVEL_CLASS[level]} />;
}

export function SeverityBadge({ severity }: { severity: ReportSeverity }) {
  return <Badge label={SEVERITY_LABEL[severity]} className={SEVERITY_CLASS[severity]} />;
}
